import {
  EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER,
  EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER_LEGACY,
  EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS,
} from "../constants";

/**
 * Builds the final GET URL for "fetch order by id" by substituting the placeholder.
 * @param {string} urlTemplate - e.g. https://partner.example.com/v1/orders/{ORDER_ID}
 * @param {string|number} orderId - internal order id to inject
 * @returns {string}
 */
export function replaceOrderIdInIntegrationUrl(urlTemplate, orderId) {
  if (urlTemplate == null || orderId == null || orderId === "") {
    return urlTemplate;
  }
  const id = encodeURIComponent(String(orderId));
  return urlTemplate
    .split(EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER_LEGACY)
    .join(id)
    .split(EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER)
    .join(id);
}

/**
 * multipart/form-data for upload_pdf_result POST (field names match client contract).
 * @param {string|number} orderId
 * @param {Blob|File} resultPdfFile
 * @returns {FormData}
 */
export function buildExternalIntegrationSubmitFormData(orderId, resultPdfFile) {
  if (resultPdfFile == null) {
    throw new TypeError("resultPdfFile is required");
  }
  const fd = new FormData();
  fd.append(
    EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.orderId,
    String(orderId)
  );
  const name =
    "name" in resultPdfFile && resultPdfFile.name
      ? resultPdfFile.name
      : "result.pdf";
  fd.append(EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.resultPdf, resultPdfFile, name);
  return fd;
}

/**
 * Converts partner `dateSampled` strings into `YYYY-MM-DD` for HTML `type="date"` inputs.
 * Handles e.g. `"09:44 01-04-2026"` (time + DD-MM-YYYY), `01-04-2026`, `01/04/2026`, and existing ISO `YYYY-MM-DD` prefixes.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeExternalDateSampledForForm(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";

  const isoPrefix = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoPrefix) {
    return `${isoPrefix[1]}-${isoPrefix[2]}-${isoPrefix[3]}`;
  }

  const withTime = s.match(/^\d{1,2}:\d{2}\s+(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (withTime) {
    const out = toIsoDateFromDMY(withTime[1], withTime[2], withTime[3]);
    return out ?? s;
  }

  const dmyDash = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyDash) {
    const out = toIsoDateFromDMY(dmyDash[1], dmyDash[2], dmyDash[3]);
    return out ?? s;
  }

  const dmySlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmySlash) {
    const out = toIsoDateFromDMY(dmySlash[1], dmySlash[2], dmySlash[3]);
    return out ?? s;
  }

  return s;
}

function toIsoDateFromDMY(dayStr, monthStr, yearStr) {
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
}

/**
 * Strips whitespace from partner phone strings so Step 1 validation (length) matches digit-only entry.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeExternalPhoneForForm(raw) {
  if (raw == null) return "";
  return String(raw).replace(/\s/g, "");
}

/**
 * Maps a partner GET response into New COC form fields (expects JSON object or { payload } / { data }).
 */
export function mergeExternalOrderIntoFormData(formData, apiResponse) {
  const src =
    apiResponse?.payload ??
    apiResponse?.data ??
    (typeof apiResponse === "object" && apiResponse !== null
      ? apiResponse
      : {});
  if (!src || typeof src !== "object") return formData;
  const next = { ...formData };
  const scalarKeys = [
    "clientName",
    "contactName",
    "email",
    "phone",
    "refId",
    "samplingFor",
    "dateSampled",
    "samplingTechnique",
    "securitySealNo",
    "propertyType",
    "address",
    "city",
    "state",
    "postcode",
    "rego",
    "regoState",
    "VIN",
  ];
  scalarKeys.forEach((k) => {
    const v = src[k];
    if (v !== undefined && v !== null && v !== "") {
      next[k] =
        k === "dateSampled"
          ? normalizeExternalDateSampledForForm(v)
          : k === "refId"
          ? String(v)
          : k === "phone"
          ? normalizeExternalPhoneForForm(v)
          : v;
    }
  });
  const refAlias = src.referenceId ?? src.externalOrderId;
  if (
    refAlias != null &&
    refAlias !== "" &&
    (next.refId === undefined || next.refId === "")
  ) {
    next.refId = String(refAlias);
  }
  if (Array.isArray(src.samples) && src.samples.length > 0) {
    next.samples = src.samples;
  }
  return next;
}

/** Flatten API list to one row per endpoint (same rules as External Integrations settings page). */
export function normalizeExternalIntegrationList(raw) {
  if (!Array.isArray(raw)) return [];
  const rows = [];
  for (const doc of raw) {
    if (Array.isArray(doc.endpoints) && doc.endpoints.length > 0) {
      doc.endpoints.forEach((ep, idx) => {
        rows.push({
          _id: doc._id,
          id: doc.id,
          name: doc.name,
          type: ep.type,
          url: ep.url,
          headers: ep.headers,
          __legacyEndpointIndex: doc.endpoints.length > 1 ? idx : undefined,
        });
      });
    } else if (doc.type && doc.url != null) {
      rows.push(doc);
    } else {
      const hdr =
        doc.defaultHeaders &&
        typeof doc.defaultHeaders === "object" &&
        Object.keys(doc.defaultHeaders).length > 0
          ? doc.defaultHeaders
          : {};
      if (doc.fetchUrl) {
        rows.push({
          _id: doc._id,
          id: doc.id,
          name: doc.name,
          type: "fetch_order_by_id",
          url: doc.fetchUrl,
          headers: hdr,
        });
      }
      if (doc.submitUrl) {
        rows.push({
          _id: doc._id,
          id: doc.id,
          name: doc.name,
          type: "upload_pdf_result",
          url: doc.submitUrl,
          headers: hdr,
        });
      }
      if (!doc.fetchUrl && !doc.submitUrl && !doc.endpoints) {
        rows.push(doc);
      }
    }
  }
  return rows;
}
